import type { ChatCard, ChatExtras } from './ports.ts';

// The parts every chat message can carry (DMs and guild chat), read safely.
const text = (value: unknown) => (typeof value === 'string' ? value : '');
const https = (value: unknown) => {
  const url = text(value);
  return url.startsWith('https://') || url.startsWith('http://127.0.0.1:') ? url : null;
};

function card(value: unknown): ChatCard | null {
  if (!value || typeof value !== 'object') return null;
  const data = value as Record<string, unknown>;
  const kind = data.kind === 'listing' || data.kind === 'shop' || data.kind === 'guild' ? data.kind : null;
  if (!kind || !text(data.id)) return null;
  return {
    kind, id: text(data.id), handle: text(data.handle) || null, title: text(data.title) || 'Guildite', subtitle: text(data.subtitle), photo: https(data.photo),
    ...(typeof data.priceMinor === 'number' ? { priceMinor: data.priceMinor } : {}),
  };
}

export function chatExtras(data: Record<string, unknown>): ChatExtras {
  const photo = data.photo && typeof data.photo === 'object' ? https((data.photo as Record<string, unknown>).url) : null;
  const voice = data.voice && typeof data.voice === 'object' ? data.voice as Record<string, unknown> : null;
  const voiceUrl = voice ? https(voice.url) : null;
  const video = data.video && typeof data.video === 'object' ? data.video as Record<string, unknown> : null;
  const videoUrl = video ? https(video.url) : null;
  const side = (value: unknown) => (typeof value === 'number' && value > 0 && value <= 10000 ? Math.round(value) : undefined);
  const link = data.link && typeof data.link === 'object' ? data.link as Record<string, unknown> : null;
  const reactions = data.reactions && typeof data.reactions === 'object' ? data.reactions as Record<string, unknown> : {};
  const place = data.location && typeof data.location === 'object' ? data.location as Record<string, unknown> : null;
  const lat = Number(place?.lat);
  const lng = Number(place?.lng);
  const once = data.viewOnce && typeof data.viewOnce === 'object' ? data.viewOnce as Record<string, unknown> : null;
  return {
    photo: photo ? { url: photo } : null,
    voice: voiceUrl ? { url: voiceUrl, seconds: Math.max(1, Math.round(Number(voice!.seconds) || 1)) } : null,
    video: videoUrl ? {
      url: videoUrl, seconds: Math.max(1, Math.round(Number(video!.seconds) || 1)),
      ...(side(video!.width) && side(video!.height) ? { width: side(video!.width), height: side(video!.height) } : {}),
    } : null,
    card: card(data.card),
    viewOnce: once && (once.kind === 'photo' || once.kind === 'video') ? {
      kind: once.kind, openedBy: once.openedBy && typeof once.openedBy === 'object' ? Object.fromEntries(Object.entries(once.openedBy as Record<string, unknown>).map(([uid, at]) => [uid, String(at)])) : {}, expired: once.expired === true,
      ...(Number(once.seconds) > 0 ? { seconds: Math.round(Number(once.seconds)) } : {}),
      ...(Number(once.width) > 0 && Number(once.height) > 0 ? { width: Math.round(Number(once.width)), height: Math.round(Number(once.height)) } : {}),
    } : null,
    location: place && Number.isFinite(lat) && Number.isFinite(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180
      ? { lat, lng, ...(Number(place.accuracy) > 0 ? { accuracy: Math.round(Number(place.accuracy)) } : {}) } : null,
    link: link && https(link.url) ? { url: https(link.url)!, host: text(link.host) } : null,
    forwarded: data.forwarded === true,
    reactions: Object.fromEntries(Object.entries(reactions).filter(([, emoji]) => typeof emoji === 'string')) as Record<string, string>,
    editedAt: text(data.editedAt) || null,
    deleted: data.deleted === true,
  };
}

export function quote(value: unknown): { id: string; authorName: string; text: string } | null {
  if (!value || typeof value !== 'object') return null;
  const data = value as Record<string, unknown>;
  return text(data.id) ? { id: text(data.id), authorName: text(data.authorName) || 'Guildite', text: text(data.text) } : null;
}

// A message with nothing besides its text (tests and placeholders).
export const NO_EXTRAS: ChatExtras = { photo: null, voice: null, video: null, location: null, viewOnce: null, card: null, link: null, forwarded: false, reactions: {}, editedAt: null, deleted: false };
